import { Alert, Button, Modal, Spin, Typography, Upload } from 'antd';
import { useEffect, useState } from 'react';
import {
  useConfirmSourceMutation,
  useUploadSourceMutation,
  type UploadEstimate,
} from '@entities/source';
import { extractErrorMessage } from '@shared/api';
import { EstimateCard } from './EstimateCard';

const { Text } = Typography;

const ACCEPT = '.pdf,.docx,.pptx,.xlsx,.txt';
const ACCEPT_HINT = 'PDF, DOCX, PPTX, XLSX, TXT — до 100 МБ';

interface Props {
  open: boolean;
  assistantId: string;
  onClose: () => void;
}

/**
 * Двухфазная загрузка документа в KB.
 *
 * Поток:
 *   1. Юзер выбирает файл → POST /knowledge/upload (фаза 1)
 *   2. Получили estimate → показываем цену + Vision-тумблер
 *   3. Юзер подтверждает → POST /sources/{id}/confirm (фаза 2)
 *   4. Закрываем модалку, polling статуса источника продолжается
 *      из карточки источника на табе «База знаний».
 *
 * Если юзер закрывает модалку до фазы 2 — source остаётся в
 * pending_confirm на бэке, через 24ч cron его удалит сам.
 */
export function UploadSourceModal({ open, assistantId, onClose }: Props) {
  const [estimate, setEstimate] = useState<UploadEstimate | null>(null);
  const [processImages, setProcessImages] = useState(true);

  const uploadMutation = useUploadSourceMutation();
  const confirmMutation = useConfirmSourceMutation(assistantId);

  // Сброс состояния при открытии/закрытии.
  useEffect(() => {
    if (open) {
      setEstimate(null);
      setProcessImages(true);
      uploadMutation.reset();
      confirmMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFileSelect = async (file: File) => {
    try {
      const result = await uploadMutation.mutateAsync({
        assistant_id: assistantId,
        file,
      });
      setEstimate(result);
    } catch {
      // Ошибка остаётся в uploadMutation.error, отрисуется ниже
    }
  };

  const handleConfirm = async () => {
    if (!estimate) return;
    try {
      await confirmMutation.mutateAsync({
        sourceId: estimate.source_id,
        process_images: processImages,
      });
      onClose();
    } catch {
      // Ошибка остаётся в confirmMutation.error
    }
  };

  const handleReset = () => {
    setEstimate(null);
    uploadMutation.reset();
  };

  const uploadError = uploadMutation.error
    ? extractErrorMessage(uploadMutation.error)
    : null;
  const confirmError = confirmMutation.error
    ? extractErrorMessage(confirmMutation.error)
    : null;

  const isUploading = uploadMutation.isPending;
  const isConfirming = confirmMutation.isPending;

  return (
    <Modal
      open={open}
      title="Загрузить документ"
      onCancel={onClose}
      destroyOnClose
      width={520}
      footer={
        estimate
          ? [
              <Button key="back" onClick={handleReset} disabled={isConfirming}>
                Выбрать другой файл
              </Button>,
              <Button
                key="confirm"
                type="primary"
                onClick={handleConfirm}
                loading={isConfirming}
              >
                Подтвердить и обработать
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                Отмена
              </Button>,
            ]
      }
    >
      {!estimate && !isUploading && (
        <>
          <Upload.Dragger
            accept={ACCEPT}
            multiple={false}
            showUploadList={false}
            beforeUpload={(file) => {
              handleFileSelect(file);
              return false; // Не запускать дефолтный xhr-загрузчик AntD
            }}
            disabled={isUploading}
          >
            <p style={{ fontSize: 14, marginBottom: 8 }}>
              Перетащите файл сюда или нажмите для выбора
            </p>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ACCEPT_HINT}
            </Text>
          </Upload.Dragger>

          {uploadError && (
            <Alert
              type="error"
              showIcon
              message={uploadError}
              style={{ marginTop: 12 }}
            />
          )}
        </>
      )}

      {isUploading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Анализируем файл...</Text>
          </div>
        </div>
      )}

      {estimate && (
        <>
          <EstimateCard
            estimate={estimate}
            processImages={processImages}
            onProcessImagesChange={setProcessImages}
          />
          {confirmError && (
            <Alert
              type="error"
              showIcon
              message={confirmError}
              style={{ marginTop: 12 }}
            />
          )}
        </>
      )}
    </Modal>
  );
}
